import React, { useEffect, useRef, useState } from 'react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import './MinimapScroll.css';

const MinimapScroll = ({ containerRef }) => {
  const minimapRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  const [visible, setVisible] = useState(false);
  const [thumbStyle, setThumbStyle] = useState({ width: 0, left: 0 });

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftStart = useRef(0);

  const updateMinimap = () => {
    if (!containerRef || !containerRef.current) return;
    const container = containerRef.current;

    const { scrollWidth, clientWidth, scrollLeft } = container;

    if (scrollWidth <= clientWidth) {
      setVisible(false);
      return;
    }
    
    // Only show if the window is smaller than our target minimum (e.g. 1280px)
    if (window.innerWidth >= 1280) {
      setVisible(false);
      return;
    }

    setVisible(true);

    const ratio = clientWidth / scrollWidth;
    const thumbWidth = ratio * 100; // in percentage
    const maxScrollLeft = scrollWidth - clientWidth;
    const scrollRatio = scrollLeft / maxScrollLeft;
    
    // Calculate the left position of the thumb in percentage relative to the track's remaining width
    const thumbLeft = scrollRatio * (100 - thumbWidth);

    setThumbStyle({
      width: `${thumbWidth}%`,
      left: `${thumbLeft}%`,
    });
  };

  useEffect(() => {
    if (!containerRef || !containerRef.current) return;
    
    const container = containerRef.current;
    
    const handleScroll = () => {
      if (!isDragging.current) {
        updateMinimap();
      }
    };

    const handleResize = () => {
      updateMinimap();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    // Initial update
    updateMinimap();

    // Sometimes the content takes a bit to load and expand the container
    const timeout = setTimeout(updateMinimap, 500);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, [containerRef]);

  const onThumbPointerDown = (e) => {
    isDragging.current = true;
    startX.current = e.clientX;
    if (containerRef && containerRef.current) {
      scrollLeftStart.current = containerRef.current.scrollLeft;
    }
    document.body.style.userSelect = 'none'; // Prevent text selection
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const onPointerMove = (e) => {
    if (!isDragging.current || !containerRef || !containerRef.current || !trackRef.current) return;
    
    const container = containerRef.current;
    const track = trackRef.current;

    const deltaX = e.clientX - startX.current;
    
    // Calculate how much deltaX in pixels on the minimap translates to scrollLeft in the container
    const { scrollWidth, clientWidth } = container;
    const trackWidth = track.clientWidth;
    
    const maxScrollLeft = scrollWidth - clientWidth;
    
    // The thumb can move within (trackWidth - thumbWidth) pixels
    const thumbPxWidth = (clientWidth / scrollWidth) * trackWidth;
    const maxThumbLeft = trackWidth - thumbPxWidth;
    
    if (maxThumbLeft <= 0) return;

    // Movement ratio
    const movementRatio = deltaX / maxThumbLeft;
    const scrollDelta = movementRatio * maxScrollLeft;
    
    container.scrollLeft = scrollLeftStart.current + scrollDelta;
    updateMinimap();
  };

  const onPointerUp = () => {
    isDragging.current = false;
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  };

  const onTrackClick = (e) => {
    if (isDragging.current || !containerRef || !containerRef.current || !trackRef.current) return;
    
    // If clicking directly on the thumb, ignore (handled by pointer down)
    if (e.target === thumbRef.current) return;

    const container = containerRef.current;
    const track = trackRef.current;
    
    const trackRect = track.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    
    const { scrollWidth, clientWidth } = container;
    const trackWidth = track.clientWidth;
    
    // We want the center of the thumb to end up at clickX
    const thumbPxWidth = (clientWidth / scrollWidth) * trackWidth;
    
    let newThumbLeft = clickX - (thumbPxWidth / 2);
    const maxThumbLeft = trackWidth - thumbPxWidth;
    
    // Clamp
    newThumbLeft = Math.max(0, Math.min(newThumbLeft, maxThumbLeft));
    
    const scrollRatio = newThumbLeft / maxThumbLeft;
    const maxScrollLeft = scrollWidth - clientWidth;
    
    container.scrollTo({
      left: scrollRatio * maxScrollLeft,
      behavior: 'smooth'
    });
  };

  if (!visible) return null;

  return (
    <div className="minimap-container" ref={minimapRef}>
      <LeftOutlined style={{ color: '#8c8c8c', fontSize: '10px', marginRight: '8px' }} />
      <div 
        className="minimap-track" 
        ref={trackRef}
        onPointerDown={onTrackClick}
      >
        <div 
          className="minimap-thumb"
          ref={thumbRef}
          style={thumbStyle}
          onPointerDown={onThumbPointerDown}
        >
           <div className="minimap-thumb-inner">
             <div className="minimap-grip"></div>
             <div className="minimap-grip"></div>
             <div className="minimap-grip"></div>
           </div>
        </div>
      </div>
      <RightOutlined style={{ color: '#8c8c8c', fontSize: '10px', marginLeft: '8px' }} />
    </div>
  );
};

export default MinimapScroll;
